<template>
    <div>
        <h2 class="font-header text-[3rem] font-bold text-primary mb-6">Career Applications</h2>

        <div class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="p-6 border-b border-creme-200 flex justify-between items-center">
                <h3 class="font-header text-[1.5rem] font-bold">Applications ({{ applications.length }})</h3>
                <button @click="() => loadApplications()"
                    class="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-body">
                    Refresh
                </button>
            </div>
            <div class="max-h-[calc(100vh-300px)] overflow-y-auto">
                <table class="w-full">
                    <thead class="bg-green-700 text-white sticky top-0">
                        <tr>
                            <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Name</th>
                            <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Email</th>
                            <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Position</th>
                            <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Status</th>
                            <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Date</th>
                            <th class="px-4 py-3 text-left font-header text-[1.5rem] font-bold">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="application in applications" :key="application.id"
                            class="border-b border-creme-200 hover:bg-creme-50 cursor-pointer"
                            @click="selectedApplication = application">
                            <td class="px-4 py-3 font-body text-body text-gray-900">{{ `${application.first_name}
                                ${application.last_name}` }}</td>
                            <td class="px-4 py-3 font-body text-body text-gray-900">{{ application.email }}</td>
                            <td class="px-4 py-3 font-body text-body text-gray-900">
                                {{ application.position_title || (application.career_id ? 'Specific Position' : 'General Application') }}
                            </td>
                            <td class="px-4 py-3 font-body text-body">
                                <span :class="getStatusClass(application.status)"
                                    class="px-2 py-1 rounded-full text-xs capitalize">
                                    {{ application.status }}
                                </span>
                            </td>
                            <td class="px-4 py-3 font-body text-body text-gray-900">{{
                                formatDate(application.created_at) }}</td>
                            <td class="px-4 py-3">
                                <button @click.stop="deleteApplication(application.id)"
                                    class="text-red-600 hover:text-red-800 font-body text-body">
                                    Delete
                                </button>
                            </td>
                        </tr>
                        <tr v-if="applications.length === 0">
                            <td colspan="6" class="px-4 py-8 text-center text-gray-500 font-body text-body">No career
                                applications yet</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-if="pagination && pagination.total_pages > 1"
                class="p-4 flex justify-between items-center border-t border-creme-200">
                <button @click="loadApplications(currentPage - 1)" :disabled="!pagination.prev_page"
                    :class="['px-4 py-2 rounded-md font-marketing', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Previous
                </button>
                <span class="font-marketing text-gray-700">
                    Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
                </span>
                <button @click="loadApplications(currentPage + 1)" :disabled="!pagination.next_page"
                    :class="['px-4 py-2 rounded-md font-marketing', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Next
                </button>
            </div>
        </div>

        <BaseModal v-model="showApplicationModal" content-class="max-w-4xl max-h-[90vh]">
            <h3 class="text-2xl font-marketing font-bold text-primary mb-6">Application Details</h3>
            <div v-if="selectedApplication" class="space-y-6">
                <!-- Position Applied For -->
                <div class="border-b border-gray-200 pb-4">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Position Applied For</h4>
                    <p class="text-lg font-sans text-primary">
                        {{ selectedApplication.position_title || (selectedApplication.career_id ? 'Specific Position' : 'General Application') }}
                    </p>
                </div>

                <!-- Personal Details Section -->
                <div class="border-b border-gray-200 pb-4">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Personal Details</h4>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label class="text-sm font-marketing font-semibold text-gray-600">First Name</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.first_name }}</p>
                        </div>
                        <div v-if="selectedApplication.middle_name">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Middle Name</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.middle_name }}</p>
                        </div>
                        <div>
                            <label class="text-sm font-marketing font-semibold text-gray-600">Last Name</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.last_name }}</p>
                        </div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div v-if="selectedApplication.email">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Email</label>
                            <p class="text-lg font-sans text-primary">
                                <a :href="`mailto:${selectedApplication.email}`" class="text-green-700 hover:underline">
                                    {{ selectedApplication.email }}
                                </a>
                            </p>
                        </div>
                        <div v-if="selectedApplication.phone">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Phone</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.phone }}</p>
                        </div>
                        <div v-if="selectedApplication.pronouns">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Pronouns</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.pronouns }}</p>
                        </div>
                    </div>
                </div>

                <!-- Address Section -->
                <div class="border-b border-gray-200 pb-4" v-if="selectedApplication.country">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Address</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div v-if="selectedApplication.address_line_1">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Address Line 1</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.address_line_1 }}</p>
                        </div>
                        <div v-if="selectedApplication.address_line_2">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Address Line 2</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.address_line_2 }}</p>
                        </div>
                        <div v-if="selectedApplication.address_line_3">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Address Line 3</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.address_line_3 }}</p>
                        </div>
                        <div v-if="selectedApplication.city">
                            <label class="text-sm font-marketing font-semibold text-gray-600">City</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.city }}</p>
                        </div>
                        <div v-if="selectedApplication.county">
                            <label class="text-sm font-marketing font-semibold text-gray-600">County</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.county }}</p>
                        </div>
                        <div v-if="selectedApplication.state">
                            <label class="text-sm font-marketing font-semibold text-gray-600">State</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.state }}</p>
                        </div>
                        <div v-if="selectedApplication.postal_code">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Postal Code</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.postal_code }}</p>
                        </div>
                        <div v-if="selectedApplication.tax_district">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Tax District</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.tax_district }}</p>
                        </div>
                        <div v-if="selectedApplication.country">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Country</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.country }}</p>
                        </div>
                    </div>
                </div>

                <!-- Education Section -->
                <div class="border-b border-gray-200 pb-4" v-if="selectedApplication.degree">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Education</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div v-if="selectedApplication.degree">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Degree</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.degree }}</p>
                        </div>
                        <div v-if="selectedApplication.school">
                            <label class="text-sm font-marketing font-semibold text-gray-600">School</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.school }}</p>
                        </div>
                        <div v-if="selectedApplication.area_of_study">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Area of Study</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.area_of_study }}</p>
                        </div>
                        <div v-if="selectedApplication.education_end_date">
                            <label class="text-sm font-marketing font-semibold text-gray-600">End Date</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.education_end_date }}</p>
                        </div>
                        <div v-if="selectedApplication.education_country">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Country</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.education_country }}</p>
                        </div>
                    </div>
                </div>

                <!-- Application Questions Section -->
                <div class="border-b border-gray-200 pb-4"
                    v-if="selectedApplication.is_at_least_18 !== null || selectedApplication.is_authorized_to_work !== null || selectedApplication.requires_sponsorship !== null">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Application Questions</h4>
                    <div class="space-y-3">
                        <div v-if="selectedApplication.is_at_least_18 !== null">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Are you at least 18 years
                                of age?</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.is_at_least_18 ? 'Yes' :
                                'No' }}</p>
                        </div>
                        <div v-if="selectedApplication.is_authorized_to_work !== null">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Legally authorized to work
                                in the United States?</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.is_authorized_to_work ?
                                'Yes' : 'No' }}</p>
                        </div>
                        <div v-if="selectedApplication.requires_sponsorship !== null">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Requires sponsorship for
                                employment visa?</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.requires_sponsorship ?
                                'Yes' : 'No' }}</p>
                        </div>
                    </div>
                </div>

                <!-- Professional Experience Section -->
                <div class="border-b border-gray-200 pb-4"
                    v-if="selectedApplication.years_of_experience || selectedApplication.current_position">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Professional Experience</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div v-if="selectedApplication.years_of_experience !== null">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Years of
                                Experience</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.years_of_experience }}</p>
                        </div>
                        <div v-if="selectedApplication.current_position">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Current Position</label>
                            <p class="text-lg font-sans text-primary">{{ selectedApplication.current_position }}</p>
                        </div>
                        <div v-if="selectedApplication.availability_date">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Availability Date</label>
                            <p class="text-lg font-sans text-primary">{{
                                formatDate(selectedApplication.availability_date) }}</p>
                        </div>
                    </div>
                </div>

                <!-- Links and Documents Section -->
                <div class="border-b border-gray-200 pb-4">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Documents & Links</h4>
                    <div class="space-y-3">
                        <div v-if="selectedApplication.resume_file_url">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Resume/CV</label>
                            <p class="text-lg font-sans text-primary">
                                <button @click="downloadResume(selectedApplication.resume_file_url)"
                                    class="text-green-700 hover:underline font-marketing">
                                    Download Resume
                                </button>
                            </p>
                        </div>
                        <div v-if="selectedApplication.cover_letter_file_url">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Cover Letter File</label>
                            <p class="text-lg font-sans text-primary">
                                <button @click="downloadResume(selectedApplication.cover_letter_file_url)"
                                    class="text-green-700 hover:underline font-marketing">
                                    Download Cover Letter
                                </button>
                            </p>
                        </div>
                        <div v-if="selectedApplication.linkedin_url">
                            <label class="text-sm font-marketing font-semibold text-gray-600">LinkedIn</label>
                            <p class="text-lg font-sans text-primary">
                                <a :href="selectedApplication.linkedin_url" target="_blank" rel="noopener noreferrer"
                                    class="text-green-700 hover:underline">
                                    {{ selectedApplication.linkedin_url }}
                                </a>
                            </p>
                        </div>
                        <div v-if="selectedApplication.portfolio_url">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Portfolio</label>
                            <p class="text-lg font-sans text-primary">
                                <a :href="selectedApplication.portfolio_url" target="_blank" rel="noopener noreferrer"
                                    class="text-green-700 hover:underline">
                                    {{ selectedApplication.portfolio_url }}
                                </a>
                            </p>
                        </div>
                        <div v-if="parsedAdditionalLinks && parsedAdditionalLinks.length > 0">
                            <label class="text-sm font-marketing font-semibold text-gray-600">Additional Links</label>
                            <div class="space-y-2 mt-2">
                                <div v-for="(link, index) in parsedAdditionalLinks" :key="index"
                                    class="bg-creme-50 p-3 rounded-md">
                                    <p v-if="link.label"
                                        class="text-sm font-marketing font-semibold text-gray-700 mb-1">
                                        {{ link.label }}
                                    </p>
                                    <a :href="link.url" target="_blank" rel="noopener noreferrer"
                                        class="text-green-700 hover:underline font-sans text-sm break-all">
                                        {{ link.url }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Cover Letter and Additional Info Section -->
                <div class="border-b border-gray-200 pb-4"
                    v-if="selectedApplication.cover_letter || selectedApplication.additional_info">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Cover Letter & Additional Info</h4>
                    <div v-if="selectedApplication.cover_letter" class="mb-4">
                        <label class="text-sm font-marketing font-semibold text-gray-600">Cover Letter</label>
                        <p class="text-base font-sans text-primary whitespace-pre-wrap bg-creme-50 p-4 rounded-md mt-2">
                            {{
                                selectedApplication.cover_letter }}</p>
                    </div>
                    <div v-if="selectedApplication.additional_info">
                        <label class="text-sm font-marketing font-semibold text-gray-600">Additional Information</label>
                        <p class="text-base font-sans text-primary whitespace-pre-wrap bg-creme-50 p-4 rounded-md mt-2">
                            {{
                                selectedApplication.additional_info }}</p>
                    </div>
                </div>

                <!-- Admin Actions Section -->
                <div class="border-b border-gray-200 pb-4">
                    <h4 class="text-lg font-marketing font-bold text-primary mb-3">Application Status</h4>
                    <div>
                        <label class="text-sm font-marketing font-semibold text-gray-600">Current Status</label>
                        <p class="mb-3">
                            <span :class="getStatusClass(selectedApplication.status)"
                                class="px-3 py-1 rounded-full text-sm capitalize inline-block">
                                {{ selectedApplication.status }}
                            </span>
                        </p>
                    </div>
                    <div>
                        <label class="text-sm font-marketing font-semibold text-gray-600">Update Status</label>
                        <select v-model="statusSelect" @change="onStatusSelectChange"
                            class="w-full px-4 py-2 border border-gray-300 rounded-md font-marketing mb-2 mt-1">
                            <option value="pending">Pending</option>
                            <option value="reviewing">Reviewing</option>
                            <option value="shortlisted">Shortlisted</option>
                            <option value="rejected">Rejected</option>
                            <option value="hired">Hired</option>
                            <option value="custom">Custom</option>
                        </select>
                        <input v-if="isCustomStatus" v-model="customStatusInput" type="text"
                            class="w-full px-4 py-2 border border-gray-300 rounded-md font-marketing"
                            placeholder="Enter custom status" maxlength="50" />
                    </div>
                    <div class="mt-4">
                        <label class="text-sm font-marketing font-semibold text-gray-600">Admin Notes</label>
                        <textarea v-model="notesUpdate" rows="4"
                            class="w-full px-4 py-2 border border-gray-300 rounded-md font-sans mt-1"
                            placeholder="Add notes about this application..."></textarea>
                    </div>
                </div>

                <!-- Submission Info -->
                <div>
                    <label class="text-sm font-marketing font-semibold text-gray-600">Application Submitted</label>
                    <p class="text-lg font-sans text-primary">{{ formatDate(selectedApplication.created_at) }}</p>
                </div>

                <!-- Action Buttons -->
                <div class="flex justify-end gap-3 pt-4 border-t border-creme-200">
                    <button @click="saveChanges"
                        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing">
                        Save Changes
                    </button>
                </div>
            </div>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { adminService } from '../../services/admin'
import BaseModal from './BaseModal.vue'
import { formatDateTime } from '../../utils/date'

const applications = ref([])
const selectedApplication = ref(null)
const statusSelect = ref('pending')
const customStatusInput = ref('')
const isCustomStatus = ref(false)
const notesUpdate = ref('')
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)

const predefinedStatuses = ['pending', 'reviewing', 'shortlisted', 'rejected', 'hired']

const showApplicationModal = computed({
    get: () => !!selectedApplication.value,
    set: (value) => {
        if (!value) selectedApplication.value = null
    }
})

const loadApplications = async (page = 1) => {
    try {
        const response = await adminService.getCareerApplications(page, limit.value)
        console.log('Career applications response:', response)
        if (response.data && response.pagination) {
            applications.value = response.data
            pagination.value = response.pagination
            currentPage.value = page
        } else {
            const data = response.data || response
            if (Array.isArray(data)) {
                applications.value = data
            } else {
                console.error('Unexpected response format:', response)
                applications.value = []
            }
            pagination.value = null
        }
    } catch (error) {
        console.error('Failed to load career applications:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
        applications.value = []
        pagination.value = null
        alert(`Failed to load career applications: ${errorMessage}`)
    }
}

const deleteApplication = async (id) => {
    if (!confirm('Are you sure you want to delete this career application?')) {
        return
    }

    try {
        await adminService.deleteCareerApplication(id)
        applications.value = applications.value.filter(a => a.id !== id)
        if (selectedApplication.value?.id === id) {
            selectedApplication.value = null
        }
        if (applications.value.length === 0 && pagination.value && currentPage.value > 1) {
            await loadApplications(currentPage.value - 1)
        } else {
            await loadApplications(currentPage.value)
        }
    } catch (error) {
        console.error('Failed to delete career application:', error)
        alert('Failed to delete career application. Please try again.')
    }
}

const onStatusSelectChange = () => {
    if (statusSelect.value === 'custom') {
        isCustomStatus.value = true
        customStatusInput.value = ''
    } else {
        isCustomStatus.value = false
        customStatusInput.value = ''
    }
}

const saveChanges = async () => {
    if (!selectedApplication.value) return

    let statusToSave = statusSelect.value

    if (statusSelect.value === 'custom') {
        const trimmedCustom = customStatusInput.value?.trim()
        if (!trimmedCustom) {
            alert('Please enter a custom status or select a predefined status')
            return
        }
        if (trimmedCustom.length > 50) {
            alert('Status must be 50 characters or less')
            return
        }
        statusToSave = trimmedCustom
    }

    try {
        await adminService.updateCareerApplication(selectedApplication.value.id, {
            status: statusToSave,
            notes: notesUpdate.value || null
        })
        selectedApplication.value.status = statusToSave
        selectedApplication.value.notes = notesUpdate.value || null

        if (!predefinedStatuses.includes(statusToSave)) {
            statusSelect.value = 'custom'
            isCustomStatus.value = true
            customStatusInput.value = statusToSave
        } else {
            statusSelect.value = statusToSave
            isCustomStatus.value = false
            customStatusInput.value = ''
        }

        await loadApplications(currentPage.value)
    } catch (error) {
        console.error('Failed to save changes:', error)
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error'
        alert(`Failed to save changes: ${errorMessage}`)
    }
}

const downloadResume = async (fileUrl) => {
    if (!fileUrl) return

    try {
        const signedUrl = await adminService.getResumeSignedUrl(fileUrl)
        if (signedUrl) {
            window.open(signedUrl, '_blank')
        } else {
            alert('Failed to generate download link. Please try again.')
        }
    } catch (error) {
        console.error('Failed to get signed URL:', error)
        alert('Failed to generate download link. Please try again.')
    }
}

const getStatusClass = (status) => {
    const classes = {
        pending: 'bg-yellow-100 text-yellow-800',
        reviewing: 'bg-blue-100 text-blue-800',
        shortlisted: 'bg-green-100 text-green-800',
        rejected: 'bg-red-100 text-red-800',
        hired: 'bg-green-200 text-green-900'
    }
    return classes[status] || 'bg-gray-100 text-gray-800'
}

const formatDate = formatDateTime

const parsedAdditionalLinks = computed(() => {
    if (!selectedApplication.value?.additional_links) return null
    try {
        const links = JSON.parse(selectedApplication.value.additional_links)
        return Array.isArray(links) && links.length > 0 ? links : null
    } catch (e) {
        console.error('Failed to parse additional links:', e)
        return null
    }
})

watch(selectedApplication, () => {
    if (selectedApplication.value) {
        const currentStatus = selectedApplication.value.status
        if (predefinedStatuses.includes(currentStatus)) {
            statusSelect.value = currentStatus
            isCustomStatus.value = false
            customStatusInput.value = ''
        } else {
            statusSelect.value = 'custom'
            isCustomStatus.value = true
            customStatusInput.value = currentStatus
        }
        notesUpdate.value = selectedApplication.value.notes || ''
    }
})

onMounted(() => {
    loadApplications(1)
})
</script>

<style scoped></style>
